import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { firestore } from '../config';
import { Organizer } from '../../../types/organizer';
import { deleteUserAuth } from '../auth';

// Collection name
const ORGANIZERS_COLLECTION = 'Organizers';

// Get all organizers
export const getAllOrganizers = async (): Promise<Record<string, Organizer>> => {
  try {
    const querySnapshot = await getDocs(collection(firestore, ORGANIZERS_COLLECTION));
    const organizers: Record<string, Organizer> = {};
    
    querySnapshot.forEach((doc) => {
      organizers[doc.id] = {
        ...doc.data(),
        id: doc.id
      } as Organizer;
    });
    
    return organizers;
  } catch (error) {
    console.error('Error fetching organizers:', error);
    throw new Error('Failed to fetch organizers');
  }
};

// Get a single organizer by ID
export const getOrganizerById = async (id: string): Promise<Organizer | null> => {
  try {
    const docRef = doc(firestore, ORGANIZERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        ...docSnap.data(),
        id: docSnap.id
      } as Organizer;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching organizer:', error);
    throw new Error('Failed to fetch organizer');
  }
};

// Create a new organizer
export const createOrganizerProfile = async (organizer: Organizer): Promise<Organizer> => {
  try {
    await setDoc(doc(firestore, ORGANIZERS_COLLECTION, organizer.id), {
      ...organizer,
      createdAt: Date.now(),
      updatedAt: Date.now()
    });
    
    return organizer;
  } catch (error) {
    console.error('Error creating organizer:', error);
    throw new Error('Failed to create organizer');
  }
};

// Update an existing organizer
export const updateOrganizerProfile = async (id: string, updates: Partial<Organizer>): Promise<void> => {
  try {
    const organizerRef = doc(firestore, ORGANIZERS_COLLECTION, id);
    
    await updateDoc(organizerRef, {
      ...updates,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error updating organizer:', error);
    throw new Error('Failed to update organizer');
  }
};

// Delete an organizer
export const deleteOrganizerProfile = async (id: string): Promise<void> => {
  try {
    const organizerRef = doc(firestore, ORGANIZERS_COLLECTION, id);
    const organizerDoc = await getDoc(organizerRef);
    
    if (organizerDoc.exists()) {
      const organizerData = organizerDoc.data() as Organizer;
      
      // Delete the user's auth account
      if (organizerData.email && organizerData.password) {
        await deleteUserAuth(organizerData.email, organizerData.password);
      }
      
      // Delete the organizer document
      await deleteDoc(organizerRef);
    } else {
      throw new Error('Organizer not found');
    }
  } catch (error) {
    console.error('Error deleting organizer:', error);
    throw new Error('Failed to delete organizer');
  }
};

// Reset organizer password
export const resetOrganizerPassword = async (id: string, newPassword: string): Promise<void> => {
  try {
    const organizerRef = doc(firestore, ORGANIZERS_COLLECTION, id);
    
    await updateDoc(organizerRef, {
      password: newPassword,
      updatedAt: Date.now()
    });
  } catch (error) {
    console.error('Error resetting password:', error);
    throw new Error('Failed to reset password');
  }
};